import { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';
import api from '@/utils/api';
import { Play, Pause, Volume2, VolumeX, Maximize, ShieldCheck, Loader2 } from 'lucide-react';

/**
 * SecureVideoPlayer — HLS.js player with:
 *  - Token-gated playlist/key/segment delivery
 *  - Rotating user watermark overlay
 *  - Right-click, drag, download, picture-in-picture disabled
 *  - Custom controls (no native download button)
 *  - Chapter jump support
 */
export default function SecureVideoPlayer({ videoId, chapters = [], onProgress, onEnded, onTimeUpdate, className = '', poster = '' }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle|loading|ready|denied|error
  const [errorMsg, setErrorMsg] = useState('');
  const [watermark, setWatermark] = useState('');
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [wmPos, setWmPos] = useState({ x: 15, y: 15 });

  // Rotate watermark position every 12s for anti-screen-record
  useEffect(() => {
    if (!watermark) return;
    const tick = () => {
      setWmPos({
        x: 5 + Math.floor(Math.random() * 75),
        y: 5 + Math.floor(Math.random() * 75),
      });
    };
    const t = setInterval(tick, 12000);
    return () => clearInterval(t);
  }, [watermark]);

  const init = useCallback(async () => {
    if (!videoId) return;
    setStatus('loading');
    try {
      const { data } = await api.post(`/video/session/${videoId}`);
      setWatermark(data.watermark || '');
      const backendBase = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') || '';
      const playlistUrl = `${backendBase}${data.playlist_url}`;

      const video = videoRef.current;
      if (!video) return;

      // Safari native HLS support
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = playlistUrl;
        setStatus('ready');
        return;
      }

      if (Hls.isSupported()) {
        if (hlsRef.current) hlsRef.current.destroy();
        const hls = new Hls({
          maxBufferLength: 30,
          enableWorker: true,
        });
        hls.loadSource(playlistUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => setStatus('ready'));
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal) {
            setStatus('error');
            setErrorMsg(data.details || 'Playback error');
          }
        });
        hlsRef.current = hls;
      } else {
        setStatus('error');
        setErrorMsg('Your browser does not support secure video playback.');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setStatus('denied');
        setErrorMsg('Enroll in this course to unlock the video');
      } else if (err.response?.status === 404) {
        setStatus('error');
        setErrorMsg('Video is still processing. Check back shortly.');
      } else {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Unable to load video');
      }
    }
  }, [videoId]);

  useEffect(() => {
    init();
    return () => {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
  }, [init]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().then(() => setPlaying(true)).catch(() => {});
    else { v.pause(); setPlaying(false); }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  const seekTo = (pct) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    v.currentTime = duration * pct;
  };

  const jumpChapter = (seconds) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = seconds;
    if (v.paused) v.play().catch(() => {});
  };

  const formatTime = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full aspect-video bg-black rounded-xl overflow-hidden group select-none ${className}`}
      onContextMenu={(e) => e.preventDefault()}
      data-testid="secure-video-player"
    >
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-20">
          <Loader2 className="animate-spin text-sacred-gold" size={32} />
          <span className="ml-3 text-cream/70 font-body text-sm">Preparing secure stream…</span>
        </div>
      )}

      {status === 'denied' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-heading/90 to-warm-black z-20 p-6">
          <ShieldCheck size={40} className="text-sacred-gold/70 mb-3" />
          <p className="text-cream font-display text-lg">{errorMsg}</p>
          <p className="text-cream/50 font-body text-xs mt-2">This content is encrypted and locked to enrolled students.</p>
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-20 p-4 text-center">
          <p className="text-cream/70 font-body text-sm">{errorMsg}</p>
        </div>
      )}

      <video
        ref={videoRef}
        className="w-full h-full"
        poster={poster}
        playsInline
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        onTimeUpdate={(e) => {
          setCurrentTime(e.currentTarget.currentTime);
          onTimeUpdate?.(e.currentTarget.currentTime);
        }}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); onEnded?.(); }}
        onProgress={(e) => onProgress?.(e.currentTarget.currentTime)}
        data-testid="secure-video-element"
      />

      {/* Rotating subtle watermark */}
      {watermark && status === 'ready' && (
        <div
          className="absolute pointer-events-none text-white/15 text-[10px] sm:text-xs font-body tracking-widest uppercase transition-all duration-1000 z-10"
          style={{
            left: `${wmPos.x}%`,
            top: `${wmPos.y}%`,
            textShadow: '0 0 2px rgba(0,0,0,0.8)',
            mixBlendMode: 'difference',
          }}
        >
          {watermark}
        </div>
      )}

      {/* Custom controls */}
      {(status === 'ready' || status === 'idle') && (
        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {/* Progress bar with chapter marks */}
          <div
            className="relative h-1.5 rounded-full bg-white/20 cursor-pointer mb-2 group/bar"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const pct = (e.clientX - rect.left) / rect.width;
              seekTo(Math.max(0, Math.min(1, pct)));
            }}
            data-testid="video-progress-bar"
          >
            <div className="absolute inset-y-0 left-0 bg-sacred-gold rounded-full" style={{ width: `${duration ? (currentTime / duration * 100) : 0}%` }} />
            {chapters.map((c, i) => (
              <div
                key={i}
                className="absolute top-0 w-0.5 h-full bg-white/60 hover:bg-white transition-colors"
                style={{ left: `${duration ? (c.time / duration * 100) : 0}%` }}
                title={c.label}
              />
            ))}
          </div>

          <div className="flex items-center gap-3 text-white/90">
            <button onClick={togglePlay} className="hover:text-sacred-gold transition-colors" data-testid="video-play-btn">
              {playing ? <Pause size={20} /> : <Play size={20} />}
            </button>
            <button onClick={toggleMute} className="hover:text-sacred-gold transition-colors" data-testid="video-mute-btn">
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <span className="text-[11px] font-body">{formatTime(currentTime)} / {formatTime(duration)}</span>
            <div className="flex-1" />
            <div className="flex items-center gap-1 text-[10px] font-body text-sacred-gold/80">
              <ShieldCheck size={12} /> ENCRYPTED
            </div>
            <button onClick={toggleFullscreen} className="hover:text-sacred-gold transition-colors" data-testid="video-fullscreen-btn">
              <Maximize size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Chapters sidebar trigger (show chips below the player) */}
      {chapters.length > 0 && status === 'ready' && (
        <div className="absolute top-2 right-2 z-10 flex flex-col gap-1 max-h-[60%] overflow-y-auto opacity-0 group-hover:opacity-100 transition-opacity">
          {chapters.slice(0, 6).map((c, i) => (
            <button
              key={i}
              onClick={() => jumpChapter(c.time)}
              className="px-2 py-0.5 text-[10px] font-body rounded-full bg-black/60 text-white/80 hover:bg-sacred-gold hover:text-black transition-colors whitespace-nowrap"
              data-testid={`chapter-chip-${i}`}
            >
              {formatTime(c.time)} · {c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
